import { useState, useEffect } from 'react';
import RetailerLayout from './RetailerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { orders as ordersApi, delivery } from '../../utils/api';
import { toast } from 'sonner';
import { 
  Receipt, Truck, MapPin, Send, Phone, RefreshCw, CheckCircle2, 
  ChefHat, Package, Clock, AlertTriangle, Bell, Calendar, Timer
} from 'lucide-react';
import DeliveryTracker from '../shared/DeliveryTracker';
import { HelpTooltip } from '../shared/HelpTooltip';

// Order status constants
const ORDER_STATUS = {
  PENDING_DRLP_ACK: 'pending_drlp_ack',
  RESENT_TO_DRLP: 'resent_to_drlp',
  DRLP_CONFIRMED: 'drlp_confirmed',
  PREPARING: 'preparing',
  READY: 'ready',
  READY_OVERDUE: 'ready_overdue',
  DISPATCHED: 'dispatched',
  DELIVERED: 'delivered',
  CANCELLED_NO_ACK: 'cancelled_no_ack',
  CANCELLED_NO_READY: 'cancelled_no_ready',
};

// Helper to format time remaining
const formatTimeRemaining = (targetTime) => {
  if (!targetTime) return null;
  const target = new Date(targetTime);
  const now = new Date();
  const diffMs = target - now;
  const diffMins = Math.round(diffMs / 60000);
  
  if (diffMins < 0) {
    return { text: `${Math.abs(diffMins)} min overdue`, overdue: true };
  } else if (diffMins < 60) {
    return { text: `${diffMins} min`, overdue: false };
  } else {
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return { text: `${hours}h ${mins}m`, overdue: false };
  }
};

// Helper to generate time options for DRLP
const generateTimeOptions = (baseTime, maxDelayMins = 30) => {
  const options = [];
  const base = new Date(baseTime);
  
  // Add original time
  options.push({ value: base.toISOString(), label: base.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' (Requested)' });
  
  // Add 15, 30 minute delay options
  for (let delay of [15, 30]) {
    if (delay <= maxDelayMins) {
      const delayed = new Date(base.getTime() + delay * 60000);
      options.push({ 
        value: delayed.toISOString(), 
        label: delayed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ` (+${delay} min)`
      });
    }
  }
  
  return options;
};

export default function RetailerOrders({ user, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedAckTime, setSelectedAckTime] = useState('');
  const [dispatching, setDispatching] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [markingReady, setMarkingReady] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({
    pickup_phone: '',
    pickup_instructions: '',
    tip_amount: ''
  });

  useEffect(() => {
    loadOrders();
    // Poll for updates every 30 seconds
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check if order needs urgent attention
  const needsUrgentAttention = (order) => {
    return [ORDER_STATUS.PENDING_DRLP_ACK, ORDER_STATUS.RESENT_TO_DRLP, ORDER_STATUS.READY_OVERDUE].includes(order.status);
  };

  // Get orders needing acknowledgment
  const pendingAckOrders = orders.filter(o => 
    [ORDER_STATUS.PENDING_DRLP_ACK, ORDER_STATUS.RESENT_TO_DRLP].includes(o.status)
  );

  // Get overdue orders
  const overdueOrders = orders.filter(o => o.status === ORDER_STATUS.READY_OVERDUE);

  // Acknowledge order
  const handleAcknowledgeOrder = async (orderId, confirmedReadyBy) => {
    setAcknowledging(true);
    try {
      await ordersApi.acknowledgeOrder(orderId, confirmedReadyBy);
      toast.success('Order acknowledged successfully');
      setAckDialogOpen(false);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge order');
    } finally {
      setAcknowledging(false);
    }
  };

  // Mark order as ready (triggers payment capture)
  const handleMarkReady = async (orderId) => {
    setMarkingReady(orderId);
    try {
      await ordersApi.markReady(orderId);
      toast.success('Order marked as ready. Payment captured.');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark order ready');
    } finally {
      setMarkingReady(null);
    }
  };

  // Update order status (legacy flow)
  const handleUpdateStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      await ordersApi.updateStatus(orderId, newStatus);
      toast.success(`Order marked as "${newStatus}"`);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Get the next status action for an order
  const getStatusActions = (order) => {
    const status = order.status;
    const actions = [];
    
    // New flow: pending_drlp_ack -> drlp_confirmed -> preparing -> ready
    if ([ORDER_STATUS.PENDING_DRLP_ACK, ORDER_STATUS.RESENT_TO_DRLP].includes(status)) {
      // Need to acknowledge first
      return []; // Actions shown via alert banner instead
    }
    
    if (status === ORDER_STATUS.DRLP_CONFIRMED || status === 'confirmed') {
      actions.push({ status: 'preparing', label: 'Start Preparing', icon: ChefHat, color: 'bg-yellow-500 hover:bg-yellow-600' });
    }
    if (status === 'preparing' || status === ORDER_STATUS.PREPARING) {
      // Ready action now uses markReady endpoint
      return []; // Mark Ready button shown separately
    }
    if (status === ORDER_STATUS.READY_OVERDUE) {
      // Can still mark ready even if overdue
      return [];
    }
    if (status === 'ready' && order.delivery_method === 'pickup') {
      actions.push({ status: 'picked_up', label: 'Customer Picked Up', icon: CheckCircle2, color: 'bg-emerald-600 hover:bg-emerald-700' });
    }
    
    return actions;
  };

  const loadOrders = async () => {
    try {
      const response = await ordersApi.list();
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const openDispatchDialog = (order) => {
    setSelectedOrder(order);
    setDispatchForm({
      pickup_phone: user?.phone || '',
      pickup_instructions: '',
      tip_amount: ''
    });
    setDispatchDialogOpen(true);
  };

  const handleDispatch = async () => {
    if (!selectedOrder) return;
    
    if (!dispatchForm.pickup_phone) {
      toast.error('Please enter store phone number');
      return;
    }

    setDispatching(true);
    try {
      const response = await delivery.dispatch({
        order_id: selectedOrder.id,
        pickup_business_name: user?.store_name || 'Store',
        pickup_address: user?.address || selectedOrder.drlp_address || '123 Store Address',
        pickup_phone_number: dispatchForm.pickup_phone,
        pickup_instructions: dispatchForm.pickup_instructions,
        dropoff_address: selectedOrder.delivery_address,
        dropoff_phone_number: selectedOrder.dac_phone || '+15555555555',
        dropoff_contact_name: selectedOrder.dac_name,
        dropoff_instructions: '',
        order_value_cents: Math.round((selectedOrder.total || 0) * 100),
        tip_cents: dispatchForm.tip_amount ? Math.round(parseFloat(dispatchForm.tip_amount) * 100) : 0
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Delivery dispatched successfully!');
        
        if (response.data.is_mock) {
          toast.info('Note: DoorDash credentials not configured. Using mock mode.', {
            duration: 5000
          });
        }
        
        setDispatchDialogOpen(false);
        loadOrders(); // Refresh orders to show updated status
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispatch delivery');
    } finally {
      setDispatching(false);
    }
  };

  const getStatusBadge = (order) => {
    const status = order.status;
    const statusConfig = {
      confirmed: { color: 'bg-blue-500', label: 'Confirmed' },
      preparing: { color: 'bg-yellow-500', label: 'Preparing' },
      ready: { color: 'bg-green-500', label: 'Ready' },
      dispatched: { color: 'bg-purple-500', label: 'Dispatched' },
      in_transit: { color: 'bg-orange-500', label: 'In Transit' },
      delivered: { color: 'bg-emerald-600', label: 'Delivered' },
      picked_up: { color: 'bg-emerald-600', label: 'Picked Up' },
      cancelled: { color: 'bg-red-500', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || { color: 'bg-gray-500', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const canDispatch = (order) => {
    // Can dispatch if: delivery method is delivery AND status is "ready" AND not already dispatched/delivered
    // Changed: Now requires order to be "ready" before dispatch
    return order.delivery_method === 'delivery' && 
           order.status === 'ready' &&
           !order.delivery_external_id && 
           !['delivered', 'cancelled', 'dispatched', 'in_transit'].includes(order.status);
  };

  // Check if order is waiting for dispatch (scheduled delivery)
  const isWaitingForReady = (order) => {
    return order.delivery_method === 'delivery' && 
           order.scheduled_delivery_time &&
           !order.delivery_external_id &&
           order.status !== 'ready';
  };

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Orders
              </h1>
              <HelpTooltip portal="retailer" category="orders" tooltipKey="main" size="md" />
            </div>
            <p className="text-gray-600 mt-1">Manage customer orders and fulfillment</p>
          </div>
          <Button variant="outline" onClick={loadOrders} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Urgent Alert: Orders Pending Acknowledgment */}
        {pendingAckOrders.length > 0 && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <AlertTitle className="text-red-800 font-semibold flex items-center gap-2">
              {pendingAckOrders.length} Order{pendingAckOrders.length > 1 ? 's' : ''} Awaiting Your Response!
              <HelpTooltip portal="retailer" category="orders" tooltipKey="timeout" size="sm" />
            </AlertTitle>
            <AlertDescription className="text-red-700">
              {pendingAckOrders.some(o => o.status === ORDER_STATUS.RESENT_TO_DRLP) ? (
                <span className="font-semibold">RESENT - Previous notification was not acknowledged. Please respond immediately or orders will be cancelled.</span>
              ) : (
                <span>Please acknowledge these orders within 5 minutes or they will be resent.</span>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {pendingAckOrders.slice(0, 3).map(order => (
                  <Button 
                    key={order.id}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => {
                      setSelectedOrder(order);
                      setSelectedAckTime(order.requested_ready_by || '');
                      setAckDialogOpen(true);
                    }}
                  >
                    <Bell className="w-4 h-4 mr-1" />
                    Order #{order.id.slice(0, 6)}
                  </Button>
                ))}
                {pendingAckOrders.length > 3 && (
                  <span className="text-red-700 text-sm self-center">+{pendingAckOrders.length - 3} more</span>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Urgent Alert: Orders Overdue for Ready */}
        {overdueOrders.length > 0 && (
          <Alert className="border-orange-200 bg-orange-50">
            <Timer className="h-5 w-5 text-orange-600" />
            <AlertTitle className="text-orange-800 font-semibold">
              {overdueOrders.length} Order{overdueOrders.length > 1 ? 's' : ''} Overdue!
            </AlertTitle>
            <AlertDescription className="text-orange-700">
              These orders have passed their ready-by time. Please mark them ready immediately or they will be cancelled.
              <div className="mt-3 flex flex-wrap gap-2">
                {overdueOrders.slice(0, 3).map(order => (
                  <Button 
                    key={order.id}
                    size="sm"
                    className="bg-orange-600 hover:bg-orange-700"
                    onClick={() => handleMarkReady(order.id)}
                    disabled={markingReady === order.id}
                  >
                    {markingReady === order.id ? (
                      <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Package className="w-4 h-4 mr-1" />
                    )}
                    Mark #{order.id.slice(0, 6)} Ready
                  </Button>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Loading orders...</p>
            </CardContent>
          </Card>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No orders yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const codePrefix = order.pickup_code_prefix || (order.delivery_method === 'delivery' ? 'DP' : 'CP');
              const codeFull = `${codePrefix}-${order.pickup_code}`;
              return (
              <Card key={order.id} data-testid={`retailer-order-${order.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`text-center min-w-[88px] rounded-lg px-2 py-1 ${codePrefix === 'DP' ? 'bg-orange-50' : 'bg-emerald-50'}`}>
                        <p className={`text-[9px] font-semibold uppercase tracking-widest ${codePrefix === 'DP' ? 'text-orange-700' : 'text-emerald-700'}`}>
                          {codePrefix === 'DP' ? 'Driver' : 'Pickup'}
                        </p>
                        <p
                          className={`text-lg font-extrabold tracking-wider ${codePrefix === 'DP' ? 'text-orange-800' : 'text-emerald-800'}`}
                          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                          data-testid={`retailer-order-code-${order.id}`}
                        >
                          {codeFull}
                        </p>
                      </div>
                      <div>
                        <CardTitle className="text-lg">Order {codeFull}</CardTitle>
                        <p className="text-sm text-gray-500">{order.dac_name}</p>
                      </div>
                    </div>
                    {getStatusBadge(order)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Delivery Method & Scheduled Time */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm">
                      {order.delivery_method === 'delivery' ? (
                        <Truck className="w-4 h-4 text-purple-600" />
                      ) : (
                        <MapPin className="w-4 h-4 text-emerald-600" />
                      )}
                      <span className="capitalize font-medium">{order.delivery_method}</span>
                      {order.delivery_method === 'pickup' && order.pickup_time && (
                        <span className="text-gray-500">• {new Date(order.pickup_time).toLocaleString()}</span>
                      )}
                      {order.delivery_method === 'delivery' && order.scheduled_delivery_time && (
                        <span className="text-purple-600 font-medium">
                          • Scheduled: {new Date(order.scheduled_delivery_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      {order.delivery_method === 'delivery' && order.delivery_type === 'asap' && (
                        <span className="text-orange-600 font-medium">• ASAP</span>
                      )}
                    </div>
                  </div>

                  {/* Scheduled Delivery Alert */}
                  {isWaitingForReady(order) && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      <div className="text-sm">
                        <p className="font-medium text-purple-800">Scheduled Delivery</p>
                        <p className="text-purple-600">
                          Mark as "Ready" to dispatch DoorDash for {new Date(order.scheduled_delivery_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} delivery
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Pending Acknowledgment Alert - Inline */}
                  {[ORDER_STATUS.PENDING_DRLP_ACK, ORDER_STATUS.RESENT_TO_DRLP].includes(order.status) && (
                    <div className={`${order.status === ORDER_STATUS.RESENT_TO_DRLP ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-3`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className={`w-5 h-5 mt-0.5 ${order.status === ORDER_STATUS.RESENT_TO_DRLP ? 'text-red-600' : 'text-amber-600'}`} />
                          <div className="text-sm">
                            <p className={`font-semibold ${order.status === ORDER_STATUS.RESENT_TO_DRLP ? 'text-red-800' : 'text-amber-800'}`}>
                              {order.status === ORDER_STATUS.RESENT_TO_DRLP ? '⚠️ RESENT — Retailer Action Required!' : 'Acknowledgment Required'}
                            </p>
                            <p className={order.status === ORDER_STATUS.RESENT_TO_DRLP ? 'text-red-700' : 'text-amber-700'}>
                              {order.requested_ready_by && (
                                <>Requested ready by: <strong>{new Date(order.requested_ready_by).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong></>
                              )}
                            </p>
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          className={order.status === ORDER_STATUS.RESENT_TO_DRLP ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
                          onClick={() => {
                            setSelectedOrder(order);
                            setSelectedAckTime(order.requested_ready_by || '');
                            setAckDialogOpen(true);
                          }}
                        >
                          <Bell className="w-4 h-4 mr-1" />
                          Acknowledge
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Ready-By Countdown for confirmed/preparing orders */}
                  {[ORDER_STATUS.DRLP_CONFIRMED, ORDER_STATUS.PREPARING, 'confirmed', 'preparing'].includes(order.status) && (order.drlp_confirmed_ready_by || order.requested_ready_by) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm">
                          <Timer className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="text-blue-800 font-medium">Ready By: {new Date(order.drlp_confirmed_ready_by || order.requested_ready_by).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                            {formatTimeRemaining(order.drlp_confirmed_ready_by || order.requested_ready_by) && (
                              <p className={`text-sm ${formatTimeRemaining(order.drlp_confirmed_ready_by || order.requested_ready_by).overdue ? 'text-red-600 font-semibold' : 'text-blue-600'}`}>
                                {formatTimeRemaining(order.drlp_confirmed_ready_by || order.requested_ready_by).text}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleMarkReady(order.id)}
                          disabled={markingReady === order.id}
                        >
                          {markingReady === order.id ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Package className="w-4 h-4 mr-1" />
                          )}
                          Mark Ready
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Ready Overdue Alert */}
                  {order.status === ORDER_STATUS.READY_OVERDUE && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm">
                          <AlertTriangle className="w-5 h-5 text-orange-600" />
                          <div>
                            <p className="text-orange-800 font-semibold">Order Overdue!</p>
                            <p className="text-orange-700">Please mark ready immediately or order will be cancelled.</p>
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={() => handleMarkReady(order.id)}
                          disabled={markingReady === order.id}
                        >
                          {markingReady === order.id ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Package className="w-4 h-4 mr-1" />
                          )}
                          Mark Ready Now
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Order Status Actions (for legacy statuses) */}
                  {!['delivered', 'picked_up', 'cancelled', 'dispatched', 'in_transit', ORDER_STATUS.PENDING_DRLP_ACK, ORDER_STATUS.RESENT_TO_DRLP, ORDER_STATUS.READY_OVERDUE].includes(order.status) && getStatusActions(order).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      {getStatusActions(order).map((action) => (
                        <Button
                          key={action.status}
                          size="sm"
                          className={action.color}
                          onClick={() => handleUpdateStatus(order.id, action.status)}
                          disabled={updatingStatus === order.id}
                          data-testid={`status-btn-${action.status}-${order.id}`}
                        >
                          {updatingStatus === order.id ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <action.icon className="w-4 h-4 mr-2" />
                          )}
                          {action.label}
                        </Button>
                      ))}
                      
                      {/* Dispatch Button - Only shows when order is ready */}
                      {canDispatch(order) && (
                        <Button 
                          size="sm" 
                          className="bg-purple-600 hover:bg-purple-700"
                          onClick={() => openDispatchDialog(order)}
                          data-testid={`dispatch-btn-${order.id}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Dispatch via DoorDash
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Delivery Address */}
                  {order.delivery_method === 'delivery' && order.delivery_address && (
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      <span className="font-medium">Deliver to: </span>
                      {order.delivery_address}
                    </div>
                  )}

                  {/* Delivery Tracking */}
                  {order.delivery_external_id && (
                    <DeliveryTracker 
                      externalDeliveryId={order.delivery_external_id}
                      orderId={order.id}
                      compact={true}
                    />
                  )}

                  {/* Order Items */}
                  <div className="space-y-1 text-sm border-t pt-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span className="text-gray-600">{item.name} x{item.quantity}</span>
                        <span className="font-medium">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Total */}
                  <div className="border-t pt-2 flex justify-between font-bold">
                    <span>Total</span>
                    <span>${(order.total || 0).toFixed(2)}</span>
                  </div>

                  {/* Order timestamp */}
                  <p className="text-xs text-gray-400">
                    Ordered: {new Date(order.created_at).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Dispatch Dialog */}
      <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Truck className="w-5 h-5 text-purple-600" />
              <span>Dispatch Delivery</span>
            </DialogTitle>
            <DialogDescription>
              Send this order to DoorDash for delivery
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Summary */}
              <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-medium">{selectedOrder.dac_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Order Total:</span>
                  <span className="font-medium">${(selectedOrder.total || 0).toFixed(2)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">Delivery Address:</span>
                  <p className="font-medium mt-1">{selectedOrder.delivery_address}</p>
                </div>
              </div>

              {/* Pickup Phone */}
              <div className="space-y-2">
                <Label htmlFor="pickup-phone">Store Phone Number *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="pickup-phone"
                    placeholder="+1 (555) 123-4567"
                    className="pl-10"
                    value={dispatchForm.pickup_phone}
                    onChange={(e) => setDispatchForm(prev => ({ ...prev, pickup_phone: e.target.value }))}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  DoorDash may call this number for pickup
                </p>
              </div>

              {/* Pickup Instructions */}
              <div className="space-y-2">
                <Label htmlFor="pickup-instructions">Pickup Instructions (Optional)</Label>
                <Input
                  id="pickup-instructions"
                  placeholder="e.g., Enter through side door, ask for manager"
                  value={dispatchForm.pickup_instructions}
                  onChange={(e) => setDispatchForm(prev => ({ ...prev, pickup_instructions: e.target.value }))}
                />
              </div>

              {/* Tip Amount */}
              <div className="space-y-2">
                <Label htmlFor="tip-amount">Add Tip for Dasher (Optional)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="tip-amount"
                    type="number"
                    step="0.50"
                    min="0"
                    placeholder="0.00"
                    className="pl-8"
                    value={dispatchForm.tip_amount}
                    onChange={(e) => setDispatchForm(prev => ({ ...prev, tip_amount: e.target.value }))}
                  />
                </div>
              </div>

              {/* Info Banner */}
              <div className="bg-purple-50 border border-purple-100 p-3 rounded-lg text-sm">
                <p className="text-purple-800">
                  <strong>Note:</strong> Once dispatched, a DoorDash driver (Dasher) will be assigned 
                  and you'll be able to track the delivery status in real-time.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDispatchDialogOpen(false)}
              disabled={dispatching}
            >
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleDispatch}
              disabled={dispatching}
            >
              {dispatching ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Dispatching...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Dispatch Delivery
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acknowledgment Dialog */}
      <Dialog open={ackDialogOpen} onOpenChange={setAckDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Bell className="w-5 h-5 text-red-600" />
              <span>Acknowledge Order</span>
            </DialogTitle>
            <DialogDescription>
              Confirm when you'll have this order ready for pickup/delivery
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              {/* Resent Warning */}
              {selectedOrder.status === ORDER_STATUS.RESENT_TO_DRLP && (
                <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                  <p className="text-red-800 font-semibold text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    RESENT - First notification was not acknowledged
                  </p>
                  <p className="text-red-700 text-sm mt-1">
                    Please respond immediately or this order will be cancelled.
                  </p>
                </div>
              )}

              {/* Order Summary */}
              <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-medium">{selectedOrder.dac_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Order Total:</span>
                  <span className="font-medium">${(selectedOrder.total || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Items:</span>
                  <span className="font-medium">{selectedOrder.items?.length || 0} item(s)</span>
                </div>
                {selectedOrder.delivery_method === 'delivery' && selectedOrder.scheduled_delivery_time && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Scheduled Delivery:</span>
                    <span className="font-medium text-purple-600">
                      {new Date(selectedOrder.scheduled_delivery_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
              </div>

              {/* Ready By Time Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  When will this order be ready?
                </Label>
                <Select 
                  value={selectedAckTime} 
                  onValueChange={setSelectedAckTime}
                >
                  <SelectTrigger data-testid="ack-time-select">
                    <SelectValue placeholder="Select a time" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedOrder.requested_ready_by && generateTimeOptions(selectedOrder.requested_ready_by).map((opt, idx) => (
                      <SelectItem key={idx} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  You can delay by up to 30 minutes from the requested time.
                  {selectedOrder.requested_ready_by && (
                    <span className="block mt-1">
                      Original request: {new Date(selectedOrder.requested_ready_by).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setAckDialogOpen(false)}
              disabled={acknowledging}
            >
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleAcknowledgeOrder(selectedOrder?.id, selectedAckTime)}
              disabled={acknowledging || !selectedAckTime}
            >
              {acknowledging ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirm Ready By Time
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </RetailerLayout>
  );
}
